import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useReducedMotion } from 'framer-motion';
import { AlbumArt } from './AlbumArt';
import { SpotifyIcon } from './icons';
import { formatMs } from '../../utils/formatMs';

const spotifyUrl = (uri) => `https://open.spotify.com/track/${uri?.split(':')[2] ?? ''}`;

export function LiveDot() {
  return (
    <span className="relative flex h-2 w-2">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-felt opacity-60
                       motion-reduce:animate-none" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-felt" />
    </span>
  );
}

// Coverflow deslizable: cada cover mide 30% del ancho; según su distancia al centro (en covers)
// se escala, se acerca al centro y se desvanece. Valores para distancia 0, 1, 2 y 3.
const SCALE = [1.3, 0.55, 0.42, 0.35];
const SHIFT = [0, 0, 55, 100]; // % del ancho del cover, hacia el centro
const OPACITY = [1, 0.75, 0.45, 0];
const lerp = (pts, d) => {
  const i = Math.min(Math.floor(d), pts.length - 2);
  return pts[i] + (pts[i + 1] - pts[i]) * Math.min(d - i, 1);
};

const LABEL = { played: 'Ya sonó', now: 'Sonando ahora', next: 'A continuación' };

function Playing({ track, recent, upNext }) {
  const [progress, setProgress] = useState(track.progressMs);
  const startRef = useRef(Date.now());

  useEffect(() => {
    setProgress(track.progressMs);
    startRef.current = Date.now();
  }, [track.uri, track.progressMs]);

  useEffect(() => {
    const id = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      setProgress(Math.min(track.progressMs + elapsed, track.durationMs));
    }, 1000);
    return () => clearInterval(id);
  }, [track.uri, track.progressMs, track.durationMs]);

  // Izquierda: las que sonaron (la más vieja al borde). Centro: la actual. Derecha: las siguientes.
  const items = [
    ...[...recent].reverse().map((t) => ({ ...t, kind: 'played' })),
    { ...track, kind: 'now' },
    ...upNext.map((t) => ({ ...t, kind: 'next' })),
  ];
  const nowIdx = recent.length;

  const scroller = useRef(null);
  const [pos, setPos] = useState(nowIdx); // posición continua del scroll, en covers
  const reduce = useReducedMotion();

  const step = () => {
    const c = scroller.current?.querySelectorAll('[data-cover]');
    return c?.length > 1 ? c[1].offsetLeft - c[0].offsetLeft : 1;
  };
  const goTo = useCallback((i, smooth) => {
    scroller.current?.scrollTo({ left: i * step(), behavior: smooth && !reduce ? 'smooth' : 'auto' });
  }, [reduce]);

  // Al entrar y cada vez que cambia el tema, vuelve a centrar el que suena.
  useLayoutEffect(() => {
    goTo(nowIdx, false);
    setPos(nowIdx);
  }, [track.uri, nowIdx, goTo]);

  const focusIdx = Math.max(0, Math.min(items.length - 1, Math.round(pos)));
  const focus = items[focusIdx];
  const isNow = focus.kind === 'now';

  // ponytail: el progreso se estima desde el poll; si pasó la duración (pausa o tema trabado)
  // se oculta en vez de dejar la barra llena en "-0:00" hasta que cambie el tema.
  const ended = track.durationMs > 0 && progress >= track.durationMs;
  const pct = track.durationMs > 0 ? Math.min((progress / track.durationMs) * 100, 100) : 0;

  return (
    <>
      <p aria-live="polite" className="flex min-h-[16px] items-center gap-2 font-mono text-[11px] uppercase
                                       tracking-[0.2em]">
        {isNow && <LiveDot />}
        <span className={isNow ? 'text-felt' : 'text-lounge-stone'}>{LABEL[focus.kind]}</span>
        {focus.mine && <span className="text-lounge-brass">· Tu canción</span>}
      </p>

      <div className="relative isolate w-full">
        <div aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-3/4 w-1/2 -translate-x-1/2
                                    -translate-y-1/2 rounded-full bg-lounge-brass/[0.07] blur-[80px]" />
        <div
          ref={scroller}
          role="region"
          aria-roledescription="carrusel"
          aria-label="Desliza para ver las que sonaron y las que siguen"
          onScroll={(e) => setPos(e.currentTarget.scrollLeft / step())}
          className="flex snap-x snap-mandatory items-center overflow-x-auto py-[11%] [scrollbar-width:none]
                     [&::-webkit-scrollbar]:hidden"
        >
          <div aria-hidden className="w-[35%] shrink-0" />
          {items.map((t, i) => {
            const d = i - pos;
            const a = Math.min(Math.abs(d), 3);
            return (
              <button
                key={`${t.kind}-${t.uri}-${i}`}
                type="button"
                data-cover
                aria-label={`${LABEL[t.kind]}: ${t.name}, de ${t.artist}`}
                aria-current={i === focusIdx || undefined}
                onClick={() => goTo(i, true)}
                style={{
                  transform: `translateX(${lerp(SHIFT, a) * -Math.sign(d)}%) scale(${lerp(SCALE, a)})`,
                  opacity: lerp(OPACITY, a),
                  zIndex: 10 - Math.round(a),
                }}
                className="relative w-[30%] shrink-0 snap-center rounded focus-visible:outline-none
                           focus-visible:ring-2 focus-visible:ring-lounge-brass"
              >
                {t.kind === 'now' && t.image && (
                  <img
                    src={t.image}
                    alt=""
                    aria-hidden
                    className="pointer-events-none absolute inset-0 -z-10 h-full w-full translate-y-3 scale-95
                               object-contain opacity-35 blur-[36px]"
                  />
                )}
                <AlbumArt image={t.image} className="w-full" />
              </button>
            );
          })}
          <div aria-hidden className="w-[35%] shrink-0" />
        </div>
      </div>

      <div className="flex w-full flex-col items-center text-center">
        <h1 className="font-serif text-[26px] font-semibold leading-tight tracking-tight text-lounge-text
                       lg:text-[36px] lg:leading-[1.1]">
          {focus.name}
        </h1>
        <p className="mt-1.5 font-inter text-base text-lounge-stone lg:text-lg">{focus.artist}</p>
        <a
          href={spotifyUrl(focus.uri)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex min-h-[44px] items-center gap-2 font-mono text-[11px] uppercase
                     tracking-[0.15em] text-lounge-stone transition-colors hover:text-lounge-text"
        >
          <SpotifyIcon className="h-4 w-4 text-[#1DB954]" />
          Abrir en Spotify
        </a>
      </div>

      {/* Solo la que suena tiene progreso; invisible (no quitado) para que el layout no salte al deslizar */}
      {!ended && <div className={`mt-3 w-full ${isNow ? '' : 'invisible'}`}>
        <div
          role="progressbar"
          aria-label="Progreso de la canción"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pct)}
          className="h-1 w-full overflow-hidden rounded-full bg-lounge-raised"
        >
          <div
            className="h-full rounded-full bg-lounge-brass shadow-brass transition-[width] duration-1000 ease-linear
                       motion-reduce:transition-none"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-2 flex justify-between font-mono text-[11px] tabular-nums text-lounge-stone">
          <span>{formatMs(progress)}</span>
          <span>-{formatMs(track.durationMs - progress)}</span>
        </div>
      </div>}
    </>
  );
}

export function NowPlaying({ track, recent = [], upNext = [] }) {
  return (
    <section aria-label="Sonando ahora" className="flex flex-col items-center">
      {track ? (
        <Playing track={track} recent={recent} upNext={upNext} />
      ) : (
        <div className="flex w-full flex-col items-center rounded-lg border border-lounge-line bg-lounge-chassis
                        px-6 py-12 text-center">
          <p className="font-serif text-xl italic text-lounge-text">La sala está en silencio</p>
          <p className="mt-2 font-inter text-sm text-lounge-stone">
            Elige una canción y sé quien abra la noche.
          </p>
        </div>
      )}
    </section>
  );
}
